/**
 * The write affordance for Case Notes (#692) on the case-detail panel.
 *
 * Notes themselves render on the panel's timeline, woven in with the routing
 * events — a case tells one chronological story, not two parallel ones. This
 * component is only the composer that adds to it.
 *
 * WHY AN "ABOUT" PICKER. What a note is about decides whose record it reaches:
 * a handling-firm note lands on that firm's page, an originating-firm note on
 * theirs, and client or LF notes on neither. That cannot be inferred from the
 * text without guessing, and a wrong guess files an observation about a client
 * inside a firm's record, where it later reads as a fact about that firm. So
 * the author declares it, always.
 *
 * WHY A FIRM PICKER TOO. A handling-firm note anchors to the placement it
 * concerns, and a matter can have been sent to three. A note about the firm
 * that declined last week belongs to that placement, not to whoever holds the
 * matter today. It shows even with one placement, so the anchor is visible
 * rather than silently assumed.
 *
 * WHY BACKDATING IS OFFERED. Writing work up days later is the normal case.
 * Without a date field the timeline would order notes by when someone did their
 * paperwork instead of when things happened. Future dates are refused server-
 * side — a note records what happened; what is planned is a Task.
 */

import { useState } from "react";
import { todayInPT, type CaseNoteAbout, type CaseNoteTarget } from "@command-center/shared";
import { useAddCaseNote } from "@/lib/use-case-notes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";

/** "Manuel Solis — Declined" so the operator can tell live from history. */
function targetLabel(t: CaseNoteTarget): string {
  const name = t.firmName ?? "Unknown firm";
  return t.decision ? `${name} — ${t.decision}` : name;
}

export function CaseNoteComposer({
  leadId,
  targets,
  testId = "case-note-composer",
}: {
  leadId: string;
  targets: CaseNoteTarget[];
  testId?: string;
}) {
  const today = todayInPT();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [about, setAbout] = useState<CaseNoteAbout>(targets.length > 0 ? "hf" : "client");
  const [placementId, setPlacementId] = useState(targets[0]?.placementId ?? "");
  const [occurredOn, setOccurredOn] = useState(today);
  const add = useAddCaseNote(leadId);

  if (!open) {
    return (
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-open`}
      >
        Add a note
      </Button>
    );
  }

  // An hf note needs a placement to anchor to; the others anchor to the matter.
  const needsPlacement = about === "hf";
  const canSubmit =
    body.trim().length > 0 && (!needsPlacement || placementId !== "") && !add.isPending;

  function reset() {
    setBody("");
    setOccurredOn(today);
    setOpen(false);
    add.reset();
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What happened? Calls, updates from the firm, a decision you made and why."
        rows={3}
        data-testid={`${testId}-body`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <NativeSelect
          value={about}
          onChange={(e) => setAbout(e.target.value as CaseNoteAbout)}
          aria-label="What this note is about"
          data-testid={`${testId}-about`}
        >
          {/* Plain words, not the stored codes — an operator should not have to
              know the schema to file a note correctly. */}
          {targets.length > 0 ? <option value="hf">About the handling firm</option> : null}
          <option value="of">About the originating firm</option>
          <option value="client">About the client</option>
          <option value="lf">Our own reasoning</option>
        </NativeSelect>
        {needsPlacement ? (
          <NativeSelect
            value={placementId}
            onChange={(e) => setPlacementId(e.target.value)}
            aria-label="Which handling firm this note is about"
            data-testid={`${testId}-target`}
          >
            {targets.map((t) => (
              <option key={t.placementId} value={t.placementId}>
                {targetLabel(t)}
              </option>
            ))}
          </NativeSelect>
        ) : null}
        <Input
          type="date"
          value={occurredOn}
          max={today}
          onChange={(e) => setOccurredOn(e.target.value)}
          aria-label="When this happened"
          className="w-auto"
          data-testid={`${testId}-date`}
        />
        <Button
          disabled={!canSubmit}
          onClick={() =>
            add.mutate(
              about === "hf"
                ? { about, placementId, body: body.trim(), occurredOn }
                : { about, body: body.trim(), occurredOn },
              { onSuccess: reset },
            )
          }
          data-testid={`${testId}-submit`}
        >
          {add.isPending ? "Saving…" : "Save note"}
        </Button>
        <Button variant="ghost" onClick={reset} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
      </div>
      {add.isError ? (
        <p className="text-xs text-destructive" data-testid={`${testId}-error`}>
          Couldn't save that note. Nothing was recorded — try again.
        </p>
      ) : null}
    </div>
  );
}
